/**
 * AISettings
 *
 * Site-wide AI configuration. Currently houses the Global Prompt —
 * a text prefix that is prepended to every bot's system prompt.
 *
 * This is Layer 1 of Rod's 3-layer PromptAssembler (AM-4013):
 *   Layer 1: Global Prompt (this)
 *   Layer 2: Bot Prompt (per AI Config)
 *   Layer 3: Channel Override (per AI Config per channel)
 */
import * as React from "react";
import { SiteSettings } from "../types";

interface Props {
  siteSettings: SiteSettings;
  setSiteSettings: React.Dispatch<React.SetStateAction<SiteSettings>>;
}

export default function AISettings({ siteSettings, setSiteSettings }: Props) {
  const charCount = siteSettings.globalPrompt?.length ?? 0;

  return (
    <div className="max-w-2xl">
      <h2 className="text-base font-semibold text-slate-800 mb-1">AI Settings</h2>
      <p className="text-sm text-slate-500 mb-6">
        Site-wide settings that apply to all AI bots and workflows.
      </p>

      {/* Global Prompt */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Global AI Prompt</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              This text is prepended to every bot's system prompt, site-wide.
              Use it for company-wide instructions like tone, compliance rules, or brand guidelines.
            </p>
          </div>
          {charCount > 0 && (
            <span className="text-xs text-slate-400 whitespace-nowrap ml-4">
              {charCount.toLocaleString()} chars
            </span>
          )}
        </div>

        <textarea
          value={siteSettings.globalPrompt ?? ""}
          onChange={(e) =>
            setSiteSettings((prev) => ({ ...prev, globalPrompt: e.target.value }))
          }
          placeholder={`Example:\nYou are a support assistant for Acme Corp. Always be professional and helpful. Never share internal pricing or competitor comparisons. If unsure, offer to connect the visitor with a human agent.`}
          rows={6}
          className="w-full text-sm border border-slate-200 rounded-lg px-4 py-3 resize-y focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300"
        />

        {/* How it works */}
        <div className="mt-4 bg-slate-50 border border-slate-100 rounded-lg px-4 py-3">
          <div className="text-xs font-medium text-slate-700 mb-2">How prompt assembly works</div>
          <div className="flex flex-col gap-2 text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center bg-violet-100 text-violet-700 rounded text-[10px] font-bold flex-shrink-0">1</span>
              <span><strong className="text-violet-700">Global Prompt</strong> — this field (applies to all bots)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center bg-blue-100 text-blue-700 rounded text-[10px] font-bold flex-shrink-0">2</span>
              <span><strong className="text-blue-700">Bot Prompt</strong> — the system prompt on each AI Config (Bots page)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center bg-amber-100 text-amber-700 rounded text-[10px] font-bold flex-shrink-0">3</span>
              <span><strong className="text-amber-700">Channel Override</strong> — per-channel tweaks (e.g., shorter for SMS, no URLs for IVR)</span>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">
            The final prompt sent to the AI is all three layers combined. Channel overrides can replace or suppress the bot prompt.
          </p>
        </div>
      </div>
    </div>
  );
}
